import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { Plus } from 'lucide-react';
import { PageHeader, StatusChip, Panel, StatStrip, ProgressRow, Avatar, FormDialog, LoadGate, PageShell, CHART_FONT } from './businessUi';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';

const KEYS = { employees: ERP_KEYS.employees };
const statusTone = { Present: 'success', Leave: 'warning', Absent: 'destructive' };
const statusDot = { Present: '#10b981', Leave: '#f59e0b', Absent: '#f43f5e' };

const ADD_FIELDS = [
  { name: 'name', label: 'Full Name', required: true },
  { name: 'role', label: 'Role', required: true, helper: 'e.g. CNC Operator' },
  { name: 'shift', label: 'Shift', type: 'select', default: 'A', options: ['A', 'B', 'C'] },
  { name: 'plant', label: 'Plant', default: 'SMC' },
  { name: 'status', label: 'Status', type: 'select', default: 'Present', options: ['Present', 'Leave', 'Absent'] },
];

export default function Workforce() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [dialogOpen, setDialogOpen] = useState(false);

  const employees = data?.employees || [];
  const handleAdd = (v) => {
    const next = [...employees, { id: `EMP-${101 + employees.length}`, ...v }];
    save('employees', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
  };

  const present = employees.filter((e) => e.status === 'Present').length;
  const onLeave = employees.filter((e) => e.status === 'Leave').length;
  const absent = employees.filter((e) => e.status === 'Absent').length;

  const attOptions = {
    chart: { type: 'donut', fontFamily: CHART_FONT, dropShadow: { enabled: true, top: 4, blur: 8, opacity: 0.12 } },
    labels: ['Present', 'On Leave', 'Absent'],
    colors: ['#10b981', '#f59e0b', '#f43f5e'],
    legend: { position: 'bottom', fontSize: '12px' },
    dataLabels: { enabled: false },
    stroke: { width: 2 },
    plotOptions: { pie: { donut: { size: '72%', labels: { show: true, total: { show: true, label: 'Attendance', fontSize: '12px', color: 'text.secondary', formatter: () => (employees.length ? `${Math.round((present / employees.length) * 100)}%` : '0%') } } } } },
  };

  const shifts = ['A', 'B', 'C'];
  const byShift = shifts.map((s) => ({ shift: s, count: employees.filter((e) => e.shift === s).length }));
  const maxShift = Math.max(1, ...byShift.map((s) => s.count));
  const shiftTone = { A: 'primary', B: 'sky', C: 'fuchsia' };

  return (
    <PageShell>
      <PageHeader
        title="Workforce"
        subtitle="Operators, shifts & attendance"
        actions={<Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>Add Employee</Button>}
      />

      <FormDialog open={dialogOpen} title="Add Employee" fields={ADD_FIELDS} onClose={() => setDialogOpen(false)} onSubmit={handleAdd} submitLabel="Add Employee" />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        <Box sx={{ mb: 2.5 }}>
          <StatStrip
            items={[
              { label: 'Headcount', value: String(employees.length), tone: 'primary' },
              { label: 'Present', value: String(present), tone: 'success' },
              { label: 'On Leave', value: String(onLeave), tone: 'warning' },
              { label: 'Absent', value: String(absent), tone: 'destructive' },
            ]}
          />
        </Box>

        {employees.length === 0 ? (
          <Panel title="Employee Directory" accent="linear-gradient(90deg,#d946ef,#e879f9)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              No employees yet. Click <strong>Add Employee</strong> to create the first record — it’s saved to ThingsBoard.
            </Typography>
          </Panel>
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, mb: 2.5 }}>
              <Panel title="Attendance Today" accent="linear-gradient(90deg,#10b981,#34d399)">
                <Box sx={{ px: 2, pb: 2 }}>
                  <ReactApexChart options={attOptions} series={[present, onLeave, absent]} type="donut" height={250} />
                </Box>
              </Panel>

              <Panel title="Headcount by Shift" subtitle="Distribution across A / B / C shifts" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
                <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
                  {byShift.map((s) => (
                    <ProgressRow key={s.shift} label={`Shift ${s.shift}`} value={s.count} max={maxShift} tone={shiftTone[s.shift]} valueLabel={`${s.count} people`} />
                  ))}
                </Box>
              </Panel>
            </Box>

            <Panel title="Employee Directory" subtitle={`${employees.length} team members`} accent="linear-gradient(90deg,#d946ef,#e879f9)">
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, p: 2 }}>
                {employees.map((e) => (
                  <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, border: '1px solid #eef2f7', bgcolor: 'background.paper' }}>
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      <Avatar sx={{ width: 44, height: 44, fontSize: 15, bgcolor: '#7c5cff' }}>
                        {e.name.split(' ').map((n) => n[0]).join('')}
                      </Avatar>
                      <Box sx={{ position: 'absolute', right: -1, bottom: -1, width: 13, height: 13, borderRadius: '50%', border: '2px solid #fff', bgcolor: statusDot[e.status] }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{e.role}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                        <StatusChip label={`Shift ${e.shift}`} tone="secondary" />
                        <StatusChip label={e.status} tone={statusTone[e.status]} />
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 10.5, color: 'text.disabled', alignSelf: 'flex-start', flexShrink: 0 }}>{e.plant}</Typography>
                  </Box>
                ))}
              </Box>
            </Panel>
          </>
        )}
      </LoadGate>
    </PageShell>
  );
}
